import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';
import { debounce } from 'lodash';
import { VoidCallback } from '../../components/common-types';

/**
 * Custom hook for tracking if an element was resized.
 * @param elementRef reference to the specific element to track
 * @param isOpen if resizing should be tracked or not
 * @param callback the callback function that should be executed when the element was resized.
 * This function is automatically wrapped with a lodash {@link debounce}.
 * @param debounceWait optional wait time passed to the lodash debounce. Passing 0 for this value will disable the debounce. Default: 1000
 * @return the {@link ResizeObserver} used for handling the element's resizing.
 */
export function useTrackElementResize(
  elementRef: RefObject<HTMLElement>,
  isOpen: boolean,
  callback: VoidCallback,
  debounceWait = 1000
): ResizeObserver {
  if (debounceWait > 0) {
    // wrap callback with a debounce to reduce the number of callback executions
    callback = debounce(callback, debounceWait);
  }

  // create the ResizeObserver
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const resizeObserver = useRef(
    new ResizeObserver(() => {
      if (elementRef.current) {
        callbackRef.current();
      }
    })
  );

  // handle observing
  useLayoutEffect(() => {
    if (isOpen) {
      if (elementRef.current) {
        resizeObserver.current.observe(elementRef.current);
      }
    } else {
      if (elementRef.current) {
        resizeObserver.current.unobserve(elementRef.current);
      }
    }
  }, [isOpen, elementRef]);

  // disconnect the ResizeObserver when the hooked component is unmounted
  useEffect(() => {
    const observer = resizeObserver.current;
    return (): void => {
      observer?.disconnect();
    };
  }, []);

  return resizeObserver.current;
}
